import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {FOLDER_LOCKING_HELP_TEXT, SEQUENCE_LOCKING_HELP_TEXT} from 'help/HelpConstants';
import * as modals from 'help/Modals';
import Tooltip from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface Props {
  archived: boolean;
  onChangeArchived?: React.ChangeEventHandler<HTMLInputElement>;
  isReadOnly: boolean;
  locked: boolean;
  onChangeLocked?: React.ChangeEventHandler<HTMLInputElement>;
  public: boolean;
  onChangePublic?: React.ChangeEventHandler<HTMLInputElement>;
  disablePublic: boolean;
  canMakePublic: boolean;
  alwaysShowAncestorFolderNames: boolean;
  onChangeAlwaysShowAncestorFolderNames?: React.ChangeEventHandler<HTMLInputElement>;
}

export const FolderLockingHelp = (): JSX.Element => (
  <div>
    <p>{FOLDER_LOCKING_HELP_TEXT}</p>
    <p>{SEQUENCE_LOCKING_HELP_TEXT}</p>
  </div>
);

const ShowHideParentFolderNamesHelp = (): JSX.Element => (
  <div>
    <p>
      By default, when a user is granted access to a folder or object, they will also be able to see the names
      of the project and any folders that contain what they've been granted. Selecting this option will hide
      the names of the project and any folders the user does not have access to.
    </p>
  </div>
);

const CustomSourceSettings = (props: Props): JSX.Element => {
  const showFolderLockingHelpModal = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    modals.showBasicHelpModal(e, 'Folder Locking', <FolderLockingHelp />);
  };
  const showHideParentFolderNamesHelp = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    modals.showBasicHelpModal(
      e,
      'Hide project and parent folder names for shared objects',
      <ShowHideParentFolderNamesHelp />
    );
  };

  return (
    <div className="u-small-spacing-bottom">
      {BenchlingConfig.ENABLE_PUBLIC_FOLDERS && props.canMakePublic && (
        <div>
          <Tooltip
            title="Setting projects as public is disabled for one of your organizations"
            disabled={!props.disablePublic}
            container={false}
          >
            <label>
              <input
                type="checkbox"
                checked={props.public}
                onChange={props.onChangePublic}
                disabled={props.disablePublic || props.isReadOnly}
                {...getTestProps({
                  component: 'CustomSourceSettings',
                  element: 'publicCheckbox',
                })}
              />
              <span>Allow anyone to view this project (make public)</span>
            </label>
          </Tooltip>
          <a onClick={modals.showPublicHelp} className="u-tiny-spacing-left">
            <BenchlingIcon icon="circle-question" />
          </a>
        </div>
      )}
      <div>
        <label>
          <input
            disabled={props.isReadOnly}
            type="checkbox"
            checked={props.locked}
            onChange={props.onChangeLocked}
            {...getTestProps({
              component: 'CustomSourceSettings',
              element: 'lockedCheckbox',
            })}
          />
          <span>Lock the sequences in this project</span>
        </label>
        <a onClick={showFolderLockingHelpModal} className="u-tiny-spacing-left">
          <BenchlingIcon icon="circle-question" />
        </a>
      </div>
      {BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS && (
        <div>
          <label>
            <input
              disabled={props.isReadOnly}
              type="checkbox"
              checked={!props.alwaysShowAncestorFolderNames}
              onChange={props.onChangeAlwaysShowAncestorFolderNames}
            />
            <span>Hide parent folder names for shared items from this project</span>
          </label>
          <a onClick={showHideParentFolderNamesHelp} className="u-tiny-spacing-left">
            <BenchlingIcon icon="circle-question" />
          </a>
        </div>
      )}
    </div>
  );
};

export default CustomSourceSettings;
